import { orderBy } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useSignal } from '../data';
import { Seatmap } from './seatmap';
import { BoundingBox, Point, Seat } from './types';

export const pricingCategoryColors = [
  '#c19e78',
  '#256d73',
  '#0cbfe9',
  '#5c92c5',
  '#ec8254',
  '#244343',
  '#6b443d',
  '#de9408',
  '#c7bebe',
  '#332f2c',
  '#eec7d6',
  '#d0f7e4',
];

export function seatName(seat: Seat): string {
  return `${seat.section.name} ${seat.row}/${seat.name}`;
}

/* HELPERS */
export function boundsOf(points: Point[]): BoundingBox {
  const point = points[0];
  if (!point) {
    return { x: 0, y: 0, width: 1, height: 1 };
  }
  let minX = point[0];
  let minY = point[1];
  let maxX = point[0];
  let maxY = point[1];
  points.forEach((e) => {
    if (minX > e[0]) {
      minX = e[0];
    } else if (maxX < e[0]) {
      maxX = e[0];
    }
    if (minY > e[1]) {
      minY = e[1];
    } else if (maxY < e[1]) {
      maxY = e[1];
    }
  });
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

export function centerOf(points: Point[]): Point {
  const bounds = boundsOf(points);
  return [bounds.x + bounds.width * 0.5, bounds.y + bounds.height * 0.5];
}

export function topLeftOf(points: Point[]): Point | null {
  if (points.length === 0) return null;
  points = orderBy(points, (e) => e[0]);
  const left = points[0][0];
  points = points.filter((e) => e[0] === left);
  return orderBy(points, (e) => e[1])[0];
}

export function useSeatmap(props: { id: string; eventId?: string }): Seatmap {
  const seatmap = useMemo(() => new Seatmap(props), [props.id, props.eventId]);
  useSignal(seatmap.signal);
  useEffect(() => {
    if (props.eventId) {
      return seatmap.pollSeatStatus();
    }
    return;
  }, [seatmap, props.eventId]);
  return seatmap;
}
